import * as React from "react";
import { Box, Button, Grid, Slide, Theme } from "@mui/material";
import OSCRTransactionDetails from "./OSCRTransactionDetails";
import OSCRSecondaryDetails from "./OSCRSecondaryDetails";
import OSCRViewBranches from "./OSCRViewBranches";
import { OscrLevelTwoRecord } from "../../interfaces";
import makeStyles from '@mui/styles/makeStyles';
import { fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl } from "../../config";
import ResultPagination from "../ResultPagination";
import usePagination from "../../hooks/usePagination";
import LoadingIndicator from "../LoadingIndicator";

interface Props {
  dataLoaded: boolean;
  timeout: number;
  showViewBranches: boolean;
  eventType: string;
  data: OscrLevelTwoRecord;
  viewBranchesData: OscrLevelTwoRecord[];
  setViewBranchesData: (viwBranchesData: any) => void;
}
const useClasses = makeStyles((theme: Theme) => ({
  viewBranchButton: {
    "height": 40,
    "width": 160,
    "&.MuiButton-root": {
      margin: "4px auto 24px auto",
      textAlign: "center",
      display: "block",
    },
  },
  absoluteCenter: {
    position: "absolute",
    left: 0,
    right: 0,
    marginLeft: "auto",
    marginRight: "auto",
    zIndex: 100,
  },
  closeBranchButton: {
    "height": 40,
    "width": 160,
    "&.MuiButton-root": {
      margin: "10px auto",
      textAlign: "center",
      display: "block",
    },
  },
  loadingSpinner: {
    marginTop: 24,
    width: "100%",
    textAlign: "center",
  },
  loadingText: {
    fontSize: "1.5rem",
    width: "100%",
    textAlign: "center",
    height: "100%",
  },
}));

export default function StructureChangeModalDialog({
  dataLoaded,
  timeout,
  showViewBranches,
  eventType,
  data,
  viewBranchesData,
  setViewBranchesData,
}: Props) {
  const classes = useClasses();

  const {
    page,
    rowsPerPage,
    handlePageJump,
    handleChangePage,
    handleChangeRowsPerPage,
  } = usePagination(1, 25);

  const [openBranch, setOpenBranch] = React.useState(false);

  const [paginationLoad, setPaginationLoad] = React.useState(false);
  React.useEffect(() => {
    if (openBranch) {
      let isMounted = true;
      setPaginationLoad(true);
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}${data.ACQ_BRANCHES_HREF}&offset=${(page - 1) *
            rowsPerPage}&limit=${rowsPerPage}`,
        )
          .then(response => {
            if (isMounted) {
              setViewBranchesData({
                resultData: response.data,
                dataLoaded: true,
                timeout: 300,
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            setPaginationLoad(false);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [
    data.ACQDATE,
    data.ACQ_BRANCHES,
    data.ACQ_BRANCHES_HREF,
    data.EFF,
    openBranch,
    page,
    rowsPerPage,
    setViewBranchesData,
  ]);

  const handleCloseViewBranches = () => {
    setViewBranchesData({ resultData: [], dataLoaded: false, timeout: 300 });
    setOpenBranch(false);
  };

  const handleOpenViewBranches = () => {
    setOpenBranch(true);
  };

  const ViewBranchesButton = (
    <Button
      id={dataLoaded ? "close-branches-button" : "view-branches-button"}
      className={
        dataLoaded ? classes.closeBranchButton : classes.viewBranchButton
      }
      variant="contained"
      color="secondary"
      disableElevation
      onClick={dataLoaded ? handleCloseViewBranches : handleOpenViewBranches}
    >
      {dataLoaded ? "Close Branches" : "View Branches"}
    </Button>
  );

  return (
    <Grid container spacing={2}>
      {!dataLoaded ? (
        <Slide
          direction="right"
          in={!dataLoaded}
          mountOnEnter
          unmountOnExit
          timeout={{ enter: timeout, exit: 0 }}
        >
          <Grid item xs={12}>
            <OSCRTransactionDetails eventType={eventType} data={data} />
            {showViewBranches ? ViewBranchesButton : ""}
            <OSCRSecondaryDetails eventType={eventType} data={data} />
          </Grid>
        </Slide>
      ) : (
        ""
      )}
      <Slide
        direction="left"
        in={dataLoaded}
        mountOnEnter
        unmountOnExit
        timeout={{ enter: timeout, exit: 0 }}
      >
        <Grid item xs={12}>
          <Box
            bgcolor="#ffffff"
            zIndex={10000}
            boxShadow="0px 2px 8px #ffffff"
            width={"100%"}
            top={-35}
            padding={"5px"}
            position="sticky"
          >
            {ViewBranchesButton}
          </Box>
          <ResultPagination
            count={Number(data.ACQ_BRANCHES)}
            handleChangePage={handleChangePage as any}
            handlePickPage={handlePageJump}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            limit={rowsPerPage}
            pageNumber={page}
            hideBottomPagination
          >
            <>
              {paginationLoad ? (
                <Box className={classes.absoluteCenter}>
                  <LoadingIndicator
                    text="Loading"
                    spinnerSize={60}
                    textClass={classes.loadingText}
                    spinnerClass={classes.loadingSpinner}
                  />
                </Box>
              ) : (
                ""
              )}
              <Box style={{ opacity: paginationLoad ? 0.1 : 1 }}>
                <OSCRViewBranches resultData={viewBranchesData} />
              </Box>
            </>
          </ResultPagination>
        </Grid>
      </Slide>
    </Grid>
  );
}
